<script setup>
import { ref, computed, watch, nextTick, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useReportBuilderAsync } from 'dashboard/composables/wgpt/useReportBuilderAsync';
import V4Button from 'next/button/Button.vue';
import SettingsFieldSection from 'next/Settings/SettingsFieldSection.vue';
import SettingsAccordion from 'next/Settings/SettingsAccordion.vue';
import InputNext from 'next/input/Input.vue';
import ReportChat from '../components/ReportChat.vue';
import ReportPreview from '../components/ReportPreview.vue';
import ConfigSummary from '../components/ConfigSummary.vue';
import CustomReportsConfigEditor from '../components/CustomReportsConfigEditor.vue';

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const { accountScopedRoute } = useAccount();

const BUILT_IN_DIMENSION_KEYS = [
  'user',
  'agent',
  'inbox',
  'contact',
  'conversation',
];

const reportName = ref('');
const editableConfig = ref({});
const chatRef = ref(null);
const uiFlags = useMapGetter('wgptCustomReports/getUIFlags');
const chatMessages = useMapGetter('wgptCustomReports/getChatMessages');
const eventDefinitions = useMapGetter(
  'wgptEventDefinitions/getEventDefinitions'
);
const latestChatConfig = useMapGetter('wgptCustomReports/getLatestChatConfig');
useReportBuilderAsync('new');

const hasConfig = computed(
  () => editableConfig.value && Object.keys(editableConfig.value).length > 0
);

const configErrors = computed(() => {
  const metricErrors = {};
  const dimensionErrors = {};
  const schemaProps = editableConfig.value?.event_schema?.properties || {};

  (editableConfig.value?.metrics || []).forEach((metric, index) => {
    if (
      metric.aggregation !== 'count' &&
      metric.property &&
      !schemaProps[metric.property]
    ) {
      metricErrors[index] = metric.label || metric.key || `Metric ${index + 1}`;
    }
  });

  (editableConfig.value?.dimensions || []).forEach((dim, index) => {
    const key = dim.property || dim.source;
    if (key && !BUILT_IN_DIMENSION_KEYS.includes(key) && !schemaProps[key]) {
      dimensionErrors[index] = dim.label || key || `Dimension ${index + 1}`;
    }
  });

  return { metrics: metricErrors, dimensions: dimensionErrors };
});

// The API rejects event properties named after the built-in dimensions; the config editor
// flags them on the property itself.
const hasReservedProperties = computed(() =>
  Object.keys(editableConfig.value?.event_schema?.properties || {}).some(key =>
    BUILT_IN_DIMENSION_KEYS.includes(key)
  )
);

const hasConfigErrors = computed(
  () =>
    Object.keys(configErrors.value.metrics).length > 0 ||
    Object.keys(configErrors.value.dimensions).length > 0 ||
    hasReservedProperties.value
);

watch(
  () => editableConfig.value?.dimensions,
  dimensions => {
    const groupBy = editableConfig.value?.default_group_by;
    if (!groupBy) return;
    const validKeys = [
      ...BUILT_IN_DIMENSION_KEYS,
      ...(dimensions || []).map(d => d.property || d.source).filter(Boolean),
    ];
    if (!validKeys.includes(groupBy)) {
      editableConfig.value = { ...editableConfig.value, default_group_by: '' };
    }
  }
);

// Scroll to bottom whenever messages change
watch(
  chatMessages,
  async () => {
    await nextTick();
    chatRef.value?.scrollToBottom();
  },
  { deep: true }
);

onMounted(() => {
  store.dispatch('wgptCustomReports/clearChatMessages');
});

function applyConfig(config) {
  reportName.value = config.report_name || reportName.value || '';
  editableConfig.value = {
    event_name: config.event_name,
    event_schema: config.event_schema || { properties: {} },
    metrics: config.metrics || [],
    dimensions: config.dimensions || [],
    default_visualization: config.default_visualization || 'bar_chart',
    default_group_by: config.default_group_by || '',
    default_time_granularity: config.default_time_granularity || 'day',
    default_value_mode: config.default_value_mode || 'absolute',
    show_summary_cards: config.show_summary_cards ?? false,
  };
}

watch(latestChatConfig, config => {
  if (config) applyConfig(config);
});

function onChatSend(message) {
  store.dispatch('wgptCustomReports/chat', {
    message,
    currentConfig: hasConfig.value ? editableConfig.value : null,
    reportId: 'new',
  });
}

const reportDescription = computed(() => {
  const firstUserMsg = chatMessages.value.find(m => m.role === 'user');
  return firstUserMsg?.content || '';
});

async function saveReport() {
  if (hasConfigErrors.value) {
    useAlert(t('WGPT_CUSTOM_REPORTS.VALIDATION.FIX_CONFIG_ERRORS'));
    return;
  }

  const config = editableConfig.value;

  let definitionId;
  try {
    const definition = await store.dispatch('wgptEventDefinitions/create', {
      name: config.event_name,
      description: reportDescription.value,
      schema: config.event_schema || { properties: {} },
    });
    definitionId = definition.id;
  } catch {
    await store.dispatch('wgptEventDefinitions/get');
    const existing = eventDefinitions.value.find(
      d => d.name === config.event_name
    );
    if (existing) {
      definitionId = existing.id;
      const existingProps = existing.schema?.properties || {};
      const incomingProps = config.event_schema?.properties || {};
      const mergedSchema = {
        properties: { ...existingProps, ...incomingProps },
      };
      await store.dispatch('wgptEventDefinitions/update', {
        id: existing.id,
        schema: mergedSchema,
      });
    } else {
      useAlert(t('WGPT_CUSTOM_REPORTS.FORM.CREATE_ERROR'));
      return;
    }
  }

  const payload = {
    name: reportName.value || config.report_name || 'Untitled Report',
    description: reportDescription.value,
    wgpt_event_definition_id: definitionId,
    config: {
      metrics: config.metrics,
      dimensions: config.dimensions,
      default_visualization: config.default_visualization,
      default_group_by: config.default_group_by,
      default_time_granularity: config.default_time_granularity,
      default_value_mode: config.default_value_mode,
      show_summary_cards: config.show_summary_cards ?? false,
    },
  };

  try {
    const report = await store.dispatch('wgptCustomReports/create', payload);
    if (report) {
      useAlert(t('WGPT_CUSTOM_REPORTS.FORM.CREATE_SUCCESS'));
      store.dispatch('wgptCustomReports/resetChat', null);
      router.push(
        accountScopedRoute('wgpt_reports_custom_show', {
          reportId: report.id,
        })
      );
    }
  } catch (error) {
    useAlert(error?.message || t('WGPT_CUSTOM_REPORTS.FORM.CREATE_ERROR'));
  }
}

function goBack() {
  router.push(accountScopedRoute('wgpt_reports_settings_list'));
}
</script>

<template>
  <section class="flex flex-col h-full w-full overflow-hidden bg-n-surface-1">
    <div class="h-20 min-h-[3.5rem] py-2 bg-n-surface-1 shrink-0">
      <div class="max-w-7xl w-full mx-auto px-6 h-full flex items-center">
        <h1 class="flex items-center mb-0 text-2xl text-n-slate-12">
          <button
            class="flex items-center p-0 font-normal cursor-pointer text-n-slate-11 text-sm ltr:mr-4 rtl:ml-4"
            @click="goBack"
          >
            <i class="i-lucide-chevron-left -ml-1 text-lg" />
            {{ t('GENERAL_SETTINGS.BACK') }}
          </button>
          <span class="text-xl font-medium text-n-slate-12">
            {{ t('WGPT_CUSTOM_REPORTS.HEADER') }}
          </span>
        </h1>
      </div>
    </div>

    <div class="border-b border-solid border-n-weak/60 shrink-0">
      <div class="max-w-7xl w-full mx-auto pt-4 pb-4 px-6">
        <div class="flex items-center justify-between">
          <h2 class="text-2xl text-n-slate-12 font-medium mb-1">
            {{ reportName || t('WGPT_CUSTOM_REPORTS.GENERATE.TITLE') }}
          </h2>
          <div class="flex gap-2 items-center">
            <V4Button
              icon="i-lucide-save"
              color="blue"
              size="sm"
              :label="t('WGPT_CUSTOM_REPORTS.GENERATE.SAVE_REPORT')"
              :is-loading="uiFlags.isCreating"
              :disabled="!hasConfig"
              @click="saveReport"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="flex-1 overflow-auto py-8">
      <div class="max-w-7xl w-full mx-auto px-6">
        <div class="flex items-start gap-6 lg:gap-10 min-h-screen">
          <div v-if="hasConfig" class="flex-1 min-w-0">
            <div class="space-y-6 pt-12 pb-6">
              <div>
                <SettingsFieldSection
                  :label="t('WGPT_CUSTOM_REPORTS.FORM.NAME')"
                >
                  <InputNext
                    v-model="reportName"
                    :placeholder="
                      t('WGPT_CUSTOM_REPORTS.FORM.NAME_PLACEHOLDER')
                    "
                  />
                </SettingsFieldSection>
              </div>

              <ReportPreview :config="editableConfig" />

              <SettingsAccordion
                :title="t('WGPT_CUSTOM_REPORTS.VIEWER.SUMMARY')"
              >
                <ConfigSummary
                  :config="editableConfig"
                  :errors="configErrors"
                  @update:config="editableConfig = $event"
                />
              </SettingsAccordion>

              <SettingsAccordion
                :title="t('WGPT_CUSTOM_REPORTS.SUMMARY.ADVANCED_SETTINGS')"
                :default-open="false"
              >
                <div class="mt-4 mb-6">
                  <label class="block text-sm font-medium text-n-slate-12 mb-1">
                    {{ t('WGPT_CUSTOM_REPORTS.GENERATE.EVENT_NAME') }}
                  </label>
                  <input
                    v-model="editableConfig.event_name"
                    type="text"
                    :placeholder="
                      t('WGPT_CUSTOM_REPORTS.FORM.EVENT_NAME_PLACEHOLDER')
                    "
                    class="w-full px-3 py-2 rounded-lg border border-n-strong bg-n-surface-1 text-n-slate-12 text-sm focus:outline-none focus:ring-2 focus:ring-n-brand"
                  />
                </div>

                <CustomReportsConfigEditor v-model="editableConfig" />
              </SettingsAccordion>
            </div>
          </div>

          <div
            class="w-2/5 shrink-0 sticky top-6 self-start flex flex-col overflow-hidden border border-n-weak rounded-2xl min-h-[calc(100vh-12rem)] mb-4"
            :class="{ 'mx-auto': !hasConfig }"
          >
            <ReportChat
              ref="chatRef"
              :messages="chatMessages"
              :is-loading="uiFlags.isChatting"
              @send="onChatSend"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
